import type { SWSettings } from "app/common/services/swSettings";
import { DefaultCellHeaderRightAlign } from "app/components/React/Table/headerCells";
import { ECategoryLeadersMetricType } from "app/pages/industry-analysis/category-leaders/CategoryLeaders.types";

import { DefaultCellRightAlign } from "../../../React/Table/cells/DefaultCellRightAlign";

// DRI-10130: dashboard widget should not show Visit duration, Pages/Visit, or Bounce rate
// (no backend data for these in the Category Leaders dashboard table context).
const CATEGORY_LEADERS_COLUMN_ORDER = [
    "Domain",
    "Share",
    "MoMChange",
    "Rank",
    "AvgMonthVisits",
    "HasAdsense",
] as const;

type CategoryLeadersMetricTypeWithConfig = Exclude<
    ECategoryLeadersMetricType,
    ECategoryLeadersMetricType.CategoryLeadersAll
>;

type CategoryLeadersColumnName = (typeof CATEGORY_LEADERS_COLUMN_ORDER)[number];

type CategoryLeadersBooleanFlag = "True" | "False";

type CategoryLeadersTableColumn = {
    name: CategoryLeadersColumnName;
    title: string;
    type: string;
    format: string;
    sortable: CategoryLeadersBooleanFlag;
    isSorted: CategoryLeadersBooleanFlag;
    sortDirection: "asc" | "desc";
    groupable: CategoryLeadersBooleanFlag;
    totalCount: CategoryLeadersBooleanFlag;
    tooltip: string | boolean;
    width: number | string;
    cellTemp?: string;
    headTemp?: string;
    fixed?: true;
    inverted?: "True";
    headerComponent?: typeof DefaultCellHeaderRightAlign;
    cellComponent?: typeof DefaultCellRightAlign;
    ppt?: {
        overrideFormat: string;
    };
};

type CategoryLeadersColumnOverrides = Partial<Record<CategoryLeadersColumnName, Partial<CategoryLeadersTableColumn>>>;

type CategoryLeadersMetricDefinition = {
    title: string;
    availabilityComponent: string;
    columnOverrides?: CategoryLeadersColumnOverrides;
};

type CategoryLeadersMetricProperties = {
    component: "CategoryLeaders";
    state: "digitalsuite_marketresearch_webmarketanalysis_rankings";
    dashboard: "true";
    title: string;
    family: "Industry";
    apiController: "CategoryLeaders";
    disableDatepicker: true;
    availabilityComponent: string;
    isHidden: (swSettings: SWSettings) => boolean;
    customProQueryParams: {
        selectedTab: string;
    };
};

type CategoryLeadersLeaderBySourceTable = {
    properties: {
        showMoreButtonItems: number;
        apiParams: {
            tab: string;
        };
        options: {
            sortedColumnAddedWidth: true;
            showLegend: false;
            dashboardSubtitleMarginBottom: 25;
            showFrame: true;
            showOverflow: true;
            desktopOnly: boolean;
        };
    };
    columns: CategoryLeadersTableColumn[];
};

type CategoryLeadersMetricConfig = {
    id: string;
    properties: CategoryLeadersMetricProperties;
    widgets: {
        single: {
            LeaderBySourceTable: CategoryLeadersLeaderBySourceTable;
        };
        compare: Record<string, never>;
    };
};

const isLeaderMetricHidden = (swSettings: SWSettings) => {
    const isLegacyUser = !swSettings.user.hasSolution2;
    if (isLegacyUser) {
        return false;
    }

    const hasMarketIntelligence = swSettings.user.hasMR;
    return !hasMarketIntelligence;
};

const BASE_CATEGORY_LEADERS_COLUMNS: Readonly<Record<CategoryLeadersColumnName, CategoryLeadersTableColumn>> = {
    Domain: {
        fixed: true,
        name: "Domain",
        title: "Domain",
        type: "string",
        format: "None",
        sortable: "False",
        isSorted: "False",
        sortDirection: "desc",
        groupable: "False",
        cellTemp: "website-tooltip-top-cell",
        headTemp: "",
        totalCount: "False",
        tooltip: "widget.table.tooltip.topsites.domain",
        width: 173,
    },
    Share: {
        name: "Share",
        title: "Traffic Share",
        type: "string",
        format: "smallNumbersPercentage:2",
        sortable: "False",
        isSorted: "False",
        sortDirection: "desc",
        groupable: "False",
        cellTemp: "default-cell",
        headTemp: "",
        totalCount: "False",
        tooltip: "widget.table.tooltip.topsites.share",
        width: "150px",
    },
    MoMChange: {
        name: "MoMChange",
        title: "Change",
        type: "double",
        format: "percentagesign",
        sortable: "True",
        isSorted: "False",
        sortDirection: "desc",
        groupable: "False",
        cellTemp: "change-percentage",
        headerComponent: DefaultCellHeaderRightAlign,
        totalCount: "False",
        tooltip: "widget.table.tooltip.topsites.change",
        width: 110,
        ppt: {
            overrideFormat: "smallNumbersPercentage:2",
        },
    },
    Rank: {
        name: "Rank",
        title: "Rank",
        type: "long",
        format: "swRank",
        sortable: "True",
        isSorted: "False",
        sortDirection: "asc",
        groupable: "False",
        cellTemp: "rank-cell",
        headerComponent: DefaultCellHeaderRightAlign,
        totalCount: "False",
        tooltip: "widget.table.tooltip.topsites.rank",
        width: 85,
        inverted: "True",
    },
    AvgMonthVisits: {
        name: "AvgMonthVisits",
        title: "Monthly Visits",
        type: "double",
        format: "abbrNumber",
        sortable: "True",
        isSorted: "False",
        sortDirection: "desc",
        groupable: "False",
        cellComponent: DefaultCellRightAlign,
        headerComponent: DefaultCellHeaderRightAlign,
        totalCount: "False",
        tooltip: "widget.table.tooltip.monthlyvisits",
        width: 130,
    },
    HasAdsense: {
        name: "HasAdsense",
        title: "Adsense",
        type: "bool",
        format: "None",
        sortable: "True",
        isSorted: "False",
        sortDirection: "desc",
        groupable: "False",
        cellTemp: "adsense-cell",
        headTemp: "",
        totalCount: "False",
        tooltip: true,
        width: 95,
    },
};

const createCategoryLeadersLeaderBySourceTableColumns = (
    definition: CategoryLeadersMetricDefinition,
): CategoryLeadersTableColumn[] => {
    return CATEGORY_LEADERS_COLUMN_ORDER.map((columnName) => {
        const baseColumn = BASE_CATEGORY_LEADERS_COLUMNS[columnName];
        const overrides = definition.columnOverrides?.[columnName];
        const column: CategoryLeadersTableColumn = {
            ...baseColumn,
            ...overrides,
        };

        if (baseColumn.ppt || overrides?.ppt) {
            const mergedPpt = {
                ...(baseColumn.ppt ?? {}),
                ...(overrides?.ppt ?? {}),
            };

            if (mergedPpt.overrideFormat) {
                column.ppt = {
                    overrideFormat: mergedPpt.overrideFormat,
                };
            }
        }

        return column;
    });
};

const createCategoryLeadersMetricProperties = (
    metricType: CategoryLeadersMetricTypeWithConfig,
    definition: CategoryLeadersMetricDefinition,
): CategoryLeadersMetricProperties => {
    return {
        component: "CategoryLeaders",
        state: "digitalsuite_marketresearch_webmarketanalysis_rankings",
        dashboard: "true",
        title: definition.title,
        family: "Industry",
        apiController: "CategoryLeaders",
        disableDatepicker: true,
        availabilityComponent: definition.availabilityComponent,
        isHidden: isLeaderMetricHidden,
        customProQueryParams: {
            selectedTab: ECategoryLeadersMetricType[metricType],
        },
    };
};

const createLeaderBySourceTable = (
    metricType: CategoryLeadersMetricTypeWithConfig,
    definition: CategoryLeadersMetricDefinition,
): CategoryLeadersLeaderBySourceTable => {
    return {
        properties: {
            showMoreButtonItems: 6,
            apiParams: {
                tab: ECategoryLeadersMetricType[metricType],
            },
            options: {
                sortedColumnAddedWidth: true,
                showLegend: false,
                dashboardSubtitleMarginBottom: 25,
                showFrame: true,
                showOverflow: true,
                desktopOnly: false,
            },
        },
        columns: createCategoryLeadersLeaderBySourceTableColumns(definition),
    };
};

function createCategoryLeadersMetricConfig(
    metricType: CategoryLeadersMetricTypeWithConfig,
    definition: CategoryLeadersMetricDefinition,
): CategoryLeadersMetricConfig {
    return {
        id: ECategoryLeadersMetricType[metricType],
        properties: createCategoryLeadersMetricProperties(metricType, definition),
        widgets: {
            single: {
                LeaderBySourceTable: createLeaderBySourceTable(metricType, definition),
            },
            compare: {},
        },
    };
}

// Keep an intermediate object so `satisfies Record<...>` forces us to define a
// config for every Category Leaders metric type except `CategoryLeadersAll`.
const categoryLeadersMetricConfigs = {
    [ECategoryLeadersMetricType.CategoryLeadersAds]: createCategoryLeadersMetricConfig(
        ECategoryLeadersMetricType.CategoryLeadersAds,
        {
            title: "dashboard.categoryLeaders.table.title.ads",
            availabilityComponent: "WsWebCategoryDisplayLeaders",
            columnOverrides: {
                AvgMonthVisits: {
                    cellTemp: "default-cell",
                },
            },
        },
    ),
    [ECategoryLeadersMetricType.CategoryLeadersDirect]: createCategoryLeadersMetricConfig(
        ECategoryLeadersMetricType.CategoryLeadersDirect,
        {
            title: "dashboard.categoryLeaders.table.title.direct",
            availabilityComponent: "WsWebCategoryDirectLeaders",
        },
    ),
    [ECategoryLeadersMetricType.CategoryLeadersMail]: createCategoryLeadersMetricConfig(
        ECategoryLeadersMetricType.CategoryLeadersMail,
        {
            title: "dashboard.categoryLeaders.table.title.mail",
            availabilityComponent: "WsWebCategoryEmailLeaders",
            columnOverrides: {
                Domain: {
                    sortable: "True",
                },
            },
        },
    ),
    [ECategoryLeadersMetricType.CategoryLeadersReferrals]: createCategoryLeadersMetricConfig(
        ECategoryLeadersMetricType.CategoryLeadersReferrals,
        {
            title: "dashboard.categoryLeaders.table.title.referrals",
            availabilityComponent: "WsWebCategoryReferralLeaders",
        },
    ),
    [ECategoryLeadersMetricType.CategoryLeadersSearch]: createCategoryLeadersMetricConfig(
        ECategoryLeadersMetricType.CategoryLeadersSearch,
        {
            title: "dashboard.categoryLeaders.table.title.search",
            availabilityComponent: "WsWebCategorySearchLeaders",
        },
    ),
    [ECategoryLeadersMetricType.CategoryLeadersSocial]: createCategoryLeadersMetricConfig(
        ECategoryLeadersMetricType.CategoryLeadersSocial,
        {
            title: "dashboard.categoryLeaders.table.title.social",
            availabilityComponent: "WsWebCategorySocialLeaders",
        },
    ),
    [ECategoryLeadersMetricType.CategoryLeadersGenAi]: createCategoryLeadersMetricConfig(
        ECategoryLeadersMetricType.CategoryLeadersGenAi,
        {
            title: "dashboard.categoryLeaders.table.title.genai",
            availabilityComponent: "WsWebCategorySearchLeaders",
        },
    ),
    [ECategoryLeadersMetricType.CategoryLeadersAffiliates]: createCategoryLeadersMetricConfig(
        ECategoryLeadersMetricType.CategoryLeadersAffiliates,
        {
            title: "dashboard.categoryLeaders.table.title.affiliates",
            availabilityComponent: "WsWebCategorySearchLeaders",
        },
    ),
} satisfies Record<CategoryLeadersMetricTypeWithConfig, CategoryLeadersMetricConfig>;

export const CategoryLeadersAds = categoryLeadersMetricConfigs[ECategoryLeadersMetricType.CategoryLeadersAds];
export const CategoryLeadersDirect = categoryLeadersMetricConfigs[ECategoryLeadersMetricType.CategoryLeadersDirect];
export const CategoryLeadersMail = categoryLeadersMetricConfigs[ECategoryLeadersMetricType.CategoryLeadersMail];
export const CategoryLeadersReferrals =
    categoryLeadersMetricConfigs[ECategoryLeadersMetricType.CategoryLeadersReferrals];
export const CategoryLeadersSearch = categoryLeadersMetricConfigs[ECategoryLeadersMetricType.CategoryLeadersSearch];
export const CategoryLeadersSocial = categoryLeadersMetricConfigs[ECategoryLeadersMetricType.CategoryLeadersSocial];
export const CategoryLeadersGenAi = categoryLeadersMetricConfigs[ECategoryLeadersMetricType.CategoryLeadersGenAi];
export const CategoryLeadersAffiliates =
    categoryLeadersMetricConfigs[ECategoryLeadersMetricType.CategoryLeadersAffiliates];

export const Subcategories = {
    id: "Subcategories",
    properties: {
        component: "IndustryAnalysis",
        state: "digitalsuite_website_websiteperformance",
        dashboard: "false",
        hasWebSource: false,
        title: "",
        family: "",
        modules: {
            Industry: {
                component: "IndustryAnalysis",
                family: "Industry",
                state: "digitalsuite_marketresearch_webmarketanalysis_overview",
                dashboard: "true",
                title: "ia.overview.subcategories",
                apiController: "IndustryAnalysis",
                availabilityComponent: "WsWebCategoryPerformance",
            },
        },
    },
    widgets: {
        single: {
            RelatedCategoriesTable: {
                properties: {
                    options: {
                        desktopOnly: true,
                        showLegend: false,
                        dashboardSubtitleMarginBottom: 15,
                    },
                    showMoreButtonItems: 5,
                },
                columns: [
                    {
                        name: "Category",
                        title: "Category",
                        type: "string",
                        format: "None",
                        sortable: "False",
                        isSorted: "False",
                        sortDirection: "desc",
                        groupable: "False",
                        cellTemp: "category-cell",
                        headTemp: "",
                        totalCount: "False",
                        tooltip: false,
                        minWidth: 230,
                        ppt: {
                            overrideFormat: "Category",
                        },
                    },
                    {
                        name: "TotalVisits",
                        title: "Total Visits",
                        type: "double",
                        format: "abbrNumber",
                        sortable: "False",
                        isSorted: "False",
                        isLink: true,
                        sortDirection: "desc",
                        groupable: "False",
                        cellTemp: "subcategories-share",
                        headerComponent: DefaultCellHeaderRightAlign,
                        totalCount: "False",
                        tooltip: false,
                        width: "",
                    },
                ],
            },
            CustomCategoriesTable: {
                properties: {
                    options: {
                        desktopOnly: true,
                        showLegend: false,
                        dashboardSubtitleMarginBottom: 15,
                    },
                    showMoreButtonItems: 5,
                },
                columns: [
                    {
                        name: "Category",
                        title: "Category",
                        type: "string",
                        format: "None",
                        sortable: "False",
                        isSorted: "False",
                        sortDirection: "desc",
                        groupable: "False",
                        cellTemp: "category-cell",
                        headTemp: "",
                        totalCount: "False",
                        tooltip: true,
                        minWidth: 230,
                        ppt: {
                            overrideFormat: "Category",
                        },
                    },
                    {
                        name: "TotalVisits",
                        title: "Total Visits",
                        type: "double",
                        format: "abbrNumber",
                        sortable: "False",
                        isSorted: "False",
                        isLink: true,
                        sortDirection: "desc",
                        groupable: "False",
                        cellTemp: "subcategories-share",
                        headerComponent: DefaultCellHeaderRightAlign,
                        totalCount: "False",
                        tooltip: true,
                        width: "",
                    },
                ],
            },
        },
    },
};
